import { Button, CommentRoundIcon, EmptyState } from '@buffer-mono/popcorn'

import type { CommentStatus } from '~publish/gql/graphql'

import { DEFAULT_STATUS } from '../../CommentsFilters/useFilters'

type StatusMessage = {
  heading: string
  description: string
}

const DEFAULT_MESSAGE: StatusMessage = {
  heading: 'No comments found',
  description: 'There are no comments to display at this time.',
}

const STATUS_MESSAGES: Partial<Record<CommentStatus | 'all', StatusMessage>> = {
  all: {
    heading: 'No comments yet',
    description: 'We will let you know when new comments appear.',
  },
  dismissed: {
    heading: 'No resolved comments',
    description: 'This is where resolved comments will appear.',
  },
  replied: {
    heading: "You haven't replied to any comments yet.",
    description: 'This is where those comments will appear.',
  },
  unreplied: {
    heading: 'No comments found',
    description: 'There are no comments to display at this time.',
  },
  new: {
    heading: 'No new comments',
    description: 'There are no new comments to display.',
  },
}

const FILTERED_MESSAGE: StatusMessage = {
  heading: 'No comments found',
  description: 'There are no comments meeting your filtering criteria.',
}

type NoCommentsFoundProps = {
  status?: CommentStatus | 'all'
  hasActiveFilters: boolean
  onClearFilters: () => void
}

export const NoCommentsFound = ({
  status = DEFAULT_STATUS,
  hasActiveFilters,
  onClearFilters,
}: NoCommentsFoundProps): JSX.Element => {
  const message = hasActiveFilters
    ? FILTERED_MESSAGE
    : STATUS_MESSAGES[status] || DEFAULT_MESSAGE

  if (!STATUS_MESSAGES[status] && process.env.NODE_ENV === 'development') {
    console.warn(
      `NoCommentsFound: No message defined for status "${status}". Using default message.`,
    )
  }

  return (
    <EmptyState size="large" variant="primary">
      <EmptyState.Icon>
        <CommentRoundIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>{message.heading}</EmptyState.Heading>
      <EmptyState.Description>{message.description}</EmptyState.Description>
      {hasActiveFilters && (
        <EmptyState.Actions>
          <Button size="large" onClick={onClearFilters}>
            Reset filters
          </Button>
        </EmptyState.Actions>
      )}
    </EmptyState>
  )
}
